import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Edit,
  Trash2,
  Eye,
  Phone,
  Calendar,
  User,
  Check,
  X,
} from "lucide-react";
import styled from "styled-components";
import { theme } from "../../styles/theme";
import { Lead } from "../../types/api";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const TableContainer = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  overflow: visible; /* permitir que dropdowns/panels flutuem sem serem cortados */
`;

const TableHeader = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[4]};
`;

const TableTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 300px;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]} ${props => props.theme.spacing[3]} ${props => props.theme.spacing[12]};
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.textTertiary};
  width: 16px;
  height: 16px;
`;

const Table = styled(motion.table)`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${props => props.theme.colors.backgroundTertiary};
`;

const TableHeaderCell = styled.th<{ $sortable?: boolean }>`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  text-align: left;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
    cursor: ${(props) => (props.$sortable ? "pointer" : "default")};
  user-select: none;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  &:hover {
      background: ${(props) => props.$sortable ? props.theme.colors.backgroundSecondary : "transparent"};
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled(motion.tr)<{ $selected?: boolean }>`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  background: ${(props) => props.$selected ? props.theme.colors.backgroundTertiary : "transparent"};

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  border-right: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-right: none;
  }
`;
 
const LeadInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const LeadAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  flex-shrink: 0;
`;

const LeadDetails = styled.div`
  flex: 1;
`;

const LeadName = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const LeadPhone = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textTertiary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borders.radius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${(props) => {
    const t = props.theme;
  switch (props.$status.toLowerCase()) {
      case "novo":
        return `
          background: rgba(0, 170, 255, 0.1);
          color: ${t.colors.info};
          border: 1px solid rgba(0, 170, 255, 0.2);
        `;
      case "qualificado":
        return `
          background: rgba(0, 255, 136, 0.1);
          color: ${t.colors.success};
          border: 1px solid rgba(0, 255, 136, 0.2);
        `;
      case "prospect":
        return `
          background: rgba(255, 170, 0, 0.1);
          color: ${t.colors.warning};
          border: 1px solid rgba(255, 170, 0, 0.2);
        `;
      case "convertido":
        return `
          background: rgba(68, 255, 68, 0.1);
          color: ${t.colors.primary};
          border: 1px solid rgba(68, 255, 68, 0.2);
        `;
      case "perdido":
        return `
          background: rgba(255, 68, 68, 0.1);
          color: ${t.colors.error};
          border: 1px solid rgba(255, 68, 68, 0.2);
        `;
      case "geral":
        return `
          background: ${t.colors.backgroundTertiary};
          color: ${t.colors.textPrimary};
          border: 1px solid ${t.colors.borderLight};
          font-weight: bold;
        `;
      default:
        return `
          background: ${t.colors.backgroundTertiary};
          color: ${t.colors.textTertiary};
          border: 1px solid ${t.colors.borderLight};
        `;
    }
  }}
`;

// ValueCell removido — coluna 'Valor Previsto' não é necessária

const DateCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[1]};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const ActionButton = styled.button<{ $variant?: "primary" | "secondary" | "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borders.radius.md};
  border: none;
  outline: none;
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  ${(props) => {
    switch (props.$variant) {
      case "primary":
        return `
          background: ${props.theme.colors.primary};
          color: #ffffff;
          &:hover {
            background: ${props.theme.colors.primaryHover};
            transform: scale(1.05);
          }
        `;
      case "danger":
        return `
          background: ${props.theme.colors.error};
          color: #ffffff;
          &:hover {
            opacity: 0.85;
            transform: scale(1.05);
          }
        `;
      case "secondary":
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          &:hover {
            background: ${props.theme.colors.backgroundSecondary};
            color: ${props.theme.colors.textPrimary};
          }
        `;
    }
  }}
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.sm};
  background: ${props => props.theme.colors.backgroundSecondary};
  cursor: pointer;
  transition: border-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut},
    background-color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  appearance: none;
  position: relative;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;

  &:checked {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 45%;
    width: 5px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const SelectionBar = styled(motion.div)`
  background: ${theme.colors.primary};
  color: ${theme.colors.textPrimary};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.colors.border};
  will-change: transform;
  transform-origin: top;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const SelectionWrapper = styled(motion.div)`
  overflow: visible;
  background: ${(props) => props.theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
`;

const SelectionButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borders.radius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${(props) => {
    const t = props.theme;
    switch (props.$variant) {
      case 'danger':
        return `
          background: ${t.colors.backgroundSecondary};
          color: ${t.colors.textTertiary};
          border-color: ${t.colors.error};
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          &:hover {
            background: ${t.colors.backgroundTertiary};
            color: ${t.colors.textSecondary};
            transform: translateY(-1px);
          }
          &:active {
            transform: translateY(0);
          }
        `;
      case 'secondary':
        return `
          background: ${t.colors.backgroundSecondary};
          color: ${t.colors.textTertiary};
          border-color: ${t.colors.border};
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          &:hover {
            background: ${t.colors.backgroundTertiary};
            color: ${t.colors.textSecondary};
            transform: translateY(-1px);
          }
          &:active {
            transform: translateY(0);
          }
        `;
      default:
        return `
          background: ${t.colors.backgroundTertiary};
          color: ${t.colors.textPrimary};
          border-color: ${t.colors.borderLight};
          &:hover {
            background: ${t.colors.backgroundSecondary};
            transform: translateY(-1px);
          }
        `;
    }
  }}
`;

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing[12]} ${props => props.theme.spacing[6]};
  text-align: center;
  min-height: 400px;
`;

const EmptyIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15 0%, ${props => props.theme.colors.primary}08 100%);
  border: 2px solid ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      ${props => props.theme.colors.primary}10 50%,
      transparent 70%
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  svg {
    color: ${props => props.theme.colors.primary};
    position: relative;
    z-index: 1;
  }
`;

const EmptyText = styled.h3`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
`;

const EmptyDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
`;

const InterestText = styled.span`
  color: ${(props) => props.theme.colors.textSecondary};
`;

interface LeadsTableProps {
  leads: Lead[];
  loading?: boolean;
  onEdit?: (lead: Lead) => void;
  onDelete?: (lead: Lead) => void;
  onBulkDeleteRequest?: (leadIds: number[]) => void;
  onView?: (lead: Lead) => void;
  onCreate?: () => void;
  clearSelectionSignal?: number;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  loading = false,
  onEdit,
  onDelete,
  onBulkDeleteRequest,
  onView,
  onCreate,
  clearSelectionSignal,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Lead>("dataCriacao");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedLeads, setSelectedLeads] = useState<Set<number>>(new Set());

  // Clear selection when requested by parent
  React.useEffect(() => {
    if (typeof clearSelectionSignal !== 'undefined') {
      setSelectedLeads(new Set());
    }
  }, [clearSelectionSignal]);

  const filteredAndSortedLeads = leads
    .filter(
      (lead) =>
        lead.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.telefone.includes(searchTerm) ||
        lead.status.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField] ?? '';
      const bValue = b[sortField] ?? '';
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const handleSort = (field: keyof Lead) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSelectLead = (leadId: number) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedLeads.size === filteredAndSortedLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredAndSortedLeads.map((lead) => lead.id)));
    }
  };

  const handleBulkDeleteRequest = () => {
    if (selectedLeads.size > 0 && onBulkDeleteRequest) {
      onBulkDeleteRequest(Array.from(selectedLeads));
    }
  };

  const clearSelection = () => {
    setSelectedLeads(new Set());
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <TableContainer>
        <TableHeader>
          <TableTitle>
            <User size={20} />
            Gerenciamento de Leads
          </TableTitle>
        </TableHeader>
        <div
          style={{
            padding: theme.spacing[8],
            textAlign: "center",
            color: theme.colors.textSecondary,
          }}
        >
          Carregando leads...
        </div>
      </TableContainer>
    );
  }

  if (leads.length === 0) {
    return (
      <TableContainer>
        <TableHeader>
          <TableTitle>
            <User size={20} />
            Gerenciamento de Leads
          </TableTitle>
          <TableActions>{/* Botão Novo Lead removido */}</TableActions>
        </TableHeader>

        <EmptyState
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <EmptyIcon>
            <User size={56} />
          </EmptyIcon>
          <EmptyText>Nenhum lead encontrado</EmptyText>
          <EmptyDescription>
            Seus leads aparecerão automaticamente aqui
          </EmptyDescription>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableHeader>
        <TableTitle>
          <User size={20} />
          Gerenciamento de Leads ({leads.length})
        </TableTitle>

        <TableActions>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              placeholder="Buscar leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          {/* Botão Novo Lead removido */}
        </TableActions>
      </TableHeader>

      <AnimatePresence initial={false} mode="wait">
        {selectedLeads.size > 0 && (
          <SelectionWrapper
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0, transition: { type: 'spring', stiffness: 200, damping: 30 } }}
            transition={{ type: 'spring', stiffness: 220, damping: 32 }}
          >
            <SelectionBar
              layout
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              exit={{ scaleY: 0, transition: { type: 'spring', stiffness: 180, damping: 28 } }}
              transition={{ type: 'spring', stiffness: 200, damping: 32 }}
            >
              <SelectionInfo>
                <Check size={16} />
                {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selecionado{selectedLeads.size > 1 ? 's' : ''}
              </SelectionInfo>
              <SelectionActions>
                <SelectionButton $variant="secondary" onClick={clearSelection}>
                  <X size={14} />
                  Cancelar
                </SelectionButton>
                {onBulkDeleteRequest && (
                  <SelectionButton $variant="danger" onClick={handleBulkDeleteRequest}>
                    <Trash2 size={14} />
                    Excluir Selecionados
                  </SelectionButton>
                )}
              </SelectionActions>
            </SelectionBar>
          </SelectionWrapper>
        )}
      </AnimatePresence>

  <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                <Checkbox
                  checked={selectedLeads.size === filteredAndSortedLeads.length && filteredAndSortedLeads.length > 0}
                  ref={(input) => {
                    if (input) input.indeterminate = selectedLeads.size > 0 && selectedLeads.size < filteredAndSortedLeads.length;
                  }}
                  onChange={handleSelectAll}
                />
                Lead
              </div>
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort("status")}>
              Status
            </TableHeaderCell>
            {/* Coluna 'Valor Previsto' removida conforme solicitado */}
            <TableHeaderCell $sortable onClick={() => handleSort("dataCriacao")}>
              Data de Criação
            </TableHeaderCell>
            <TableHeaderCell
              $sortable
              onClick={() => handleSort("ultimoContato")}
            >
              Último Contato
            </TableHeaderCell>
            <TableHeaderCell>Interesse</TableHeaderCell>
            <TableHeaderCell>Ações</TableHeaderCell>
          </tr>
        </TableHead>

        <TableBody>
            {filteredAndSortedLeads.map((lead, index) => (
              <TableRow
                key={lead.id}
                $selected={selectedLeads.has(lead.id)}
              >
              <TableCell>
                <LeadInfo>
                  <Checkbox
                    checked={selectedLeads.has(lead.id)}
                    onChange={() => handleSelectLead(lead.id)}
                  />
                  <LeadAvatar>{getInitials(lead.nome)}</LeadAvatar>
                  <LeadDetails>
                    <LeadName>{lead.nome}</LeadName>
                    <LeadPhone>
                      <Phone size={12} />
                      {lead.telefone}
                    </LeadPhone>
                  </LeadDetails>
                </LeadInfo>
              </TableCell>

              <TableCell>
                <StatusBadge $status={lead.status}>{lead.status}</StatusBadge>
              </TableCell>

              {/* Coluna 'Valor Previsto' removida */}

              <TableCell>
                <DateCell>
                  <Calendar size={14} />
                  {format(new Date(lead.dataCriacao), "dd/MM/yyyy", {
                    locale: ptBR,
                  })}
                </DateCell>
              </TableCell>

              <TableCell>
                <DateCell>
                  <Calendar size={14} />
                  {format(new Date(lead.ultimoContato), "dd/MM/yyyy", {
                    locale: ptBR,
                  })}
                </DateCell>
              </TableCell>

              <TableCell>
                <InterestText>{lead.interesse || "N/A"}</InterestText>
              </TableCell>

              <TableCell>
                <ActionsCell>
                  <ActionButton
                    $variant="secondary"
                    type="button"
                    onClick={() => onView?.(lead)}
                    title="Visualizar"
                  >
                    <Eye size={14} />
                  </ActionButton>
                  <ActionButton
                    $variant="primary"
                    type="button"
                    onClick={() => onEdit?.(lead)}
                    title="Editar"
                  >
                    <Edit size={14} />
                  </ActionButton>
                  <ActionButton
                    $variant="danger"
                    type="button"
                    onClick={() => onDelete?.(lead)}
                    title="Excluir"
                  >
                    <Trash2 size={14} />
                  </ActionButton>
                </ActionsCell>
              </TableCell>
            </TableRow>
      ))}
    </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LeadsTable;
